import { useState, memo } from "react";
import type { ChatMessage } from "@/domain/aiAgent";
import Markdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { Database, BookOpen, ChevronDown, ChevronUp, Link } from "lucide-react";
import { AiAgentFeedback } from "./AiAgentFeedback";

interface AiAgentMessageBubbleProps {
	message: ChatMessage;
	onSubmitFeedback?: (
		messageId: string,
		rating: "positive" | "negative",
		reason?: string,
		comment?: string,
	) => void;
}

const ToolBadge = ({ toolUsed }: { toolUsed: ChatMessage["toolUsed"] }) => {
	const { t } = useTranslation();

	if (!toolUsed) return null;

	const config = {
		sql_query: {
			icon: Database,
			label: t("agent.tool_sql_query"),
		},
		knowledge_base: {
			icon: BookOpen,
			label: t("agent.tool_knowledge_base"),
		},
	};

	const { icon: Icon, label } = config[toolUsed];

	return (
		<div className="ai-chat-tool-badge">
			<Icon className="ai-chat-tool-badge-icon" />
			<span>{label}</span>
		</div>
	);
};

const formatSourceName = (source: string): string => {
	// Remove gs:// paths, file extensions, and clean up names
	let name = source
		.replace(/^gs:\/\/[^/]+\/[^/]+\//, "") // Remove GCS path
		.replace(/\.(pdf|txt|md|docx)$/i, "") // Remove extensions
		.replace(/_/g, " ") // Replace underscores with spaces
		.replace(/^\d+[-_]/, ""); // Remove leading numbers

	// Truncate if too long
	if (name.length > 30) {
		name = name.substring(0, 27) + "...";
	}

	return name;
};

const SourcesList = ({ sources }: { sources?: string[] }) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);

	if (!sources || sources.length === 0) return null;

	const uniqueSources = [...new Set(sources.map(formatSourceName))];

	return (
		<div className="ai-chat-sources">
			<button
				type="button"
				className="ai-chat-sources-toggle"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<Link className="ai-chat-sources-toggle-icon" />
				<span>{t("agent.sources_count", { count: uniqueSources.length })}</span>
				{isExpanded ? (
					<ChevronUp className="ai-chat-sources-toggle-icon" />
				) : (
					<ChevronDown className="ai-chat-sources-toggle-icon" />
				)}
			</button>
			{isExpanded && (
				<div className="ai-chat-sources-list">
					{uniqueSources.map((source) => (
						<span key={source} className="ai-chat-source-item">
							{source}
						</span>
					))}
				</div>
			)}
		</div>
	);
};

const AiAgentMessageBubbleComponent = ({
	message,
	onSubmitFeedback,
}: AiAgentMessageBubbleProps) => {
	const { t } = useTranslation();

	const content =
		message.role === "error" && message.content.startsWith("agent.")
			? t(message.content)
			: message.content;

	if (message.role === "user") {
		return (
			<div className="ai-chat-msg-user">
				<div className="ai-chat-msg-user-content">{content}</div>
			</div>
		);
	}

	if (message.role === "error") {
		return (
			<div className="ai-chat-msg-assistant">
				<div className="ai-chat-msg-error-content">{content}</div>
			</div>
		);
	}

	return (
		<div className="ai-chat-msg-assistant group">
			<ToolBadge toolUsed={message.toolUsed} />
			<div className="ai-chat-msg-assistant-content ai-chat-markdown">
				<Markdown>{content}</Markdown>
			</div>
			<SourcesList sources={message.sources} />
			{message.queryId && onSubmitFeedback && (
				<AiAgentFeedback
					messageId={message.id}
					currentFeedback={message.feedback}
					onSubmitFeedback={onSubmitFeedback}
				/>
			)}
		</div>
	);
};

export const AiAgentMessageBubble = memo(AiAgentMessageBubbleComponent);
